/**
 * Reminder policy types — mirrors backend DTOs from
 * `apps/operations/src/modules/reminders/policy/dto/*` and the JSON shapes in
 * `apps/operations/src/modules/reminders/interfaces/reminder-policy.interfaces.ts`.
 */

export type ReminderType =
  | 'JOB_CONFIRMATION'
  | 'JOB_REMINDER'
  | 'ESTIMATE_FOLLOWUP'
  | 'PROPOSAL_FOLLOWUP'
  | 'UNPAID_INVOICE'
  | 'UNPAID_DEPOSIT'
  | 'REVIEW_REQUEST'
  | 'PARTS_ORDERED'
  | 'PARTS_ARRIVED';

export const REMINDER_TYPES: ReminderType[] = [
  'JOB_CONFIRMATION',
  'JOB_REMINDER',
  'ESTIMATE_FOLLOWUP',
  'PROPOSAL_FOLLOWUP',
  'UNPAID_INVOICE',
  'UNPAID_DEPOSIT',
  'REVIEW_REQUEST',
  'PARTS_ORDERED',
  'PARTS_ARRIVED',
];

/**
 * NOT-576 — `REVIEW_REQUEST` is configured on Settings > Reviews, not under
 * Settings > Reminders, so the one policy is no longer editable from two
 * screens. It stays a first-class `ReminderType`: the Reviews tab reads and
 * writes it through the very same `/v2/reminder-policies/REVIEW_REQUEST`
 * endpoints, and `REMINDER_TYPES` still mirrors the backend enum in full. Only
 * the Reminders *navigation* drops it.
 */
export const REVIEW_REQUEST_POLICY_TYPE = 'REVIEW_REQUEST' satisfies ReminderType;

/**
 * The permissions that let an operator READ the `REVIEW_REQUEST` policy — the
 * trio Settings > Reminders used, carried over unchanged by the move so nobody's
 * view of the policy narrows.
 *
 * Shared rather than spelled out twice: the Settings > Reviews nav gate
 * (`components/layout/nav-config.ts`) and the Reminder policy tab's own gate
 * (`routes/_dashboard/dashboard/settings/reviews.tsx`) must stay in step, or the
 * entry is visible to somebody the screen shows nothing to, or hidden from
 * somebody it still serves.
 */
export const REVIEW_POLICY_VIEW_PERMISSIONS = [
  'reminders:view',
  'reminders:edit',
  'company:view',
] as const;

/**
 * The reminder types that Settings > Reminders offers a tab for — every type
 * except the review request, which is configured on Settings > Reviews.
 *
 * Narrowed at the type level rather than filtered at runtime, so the Reminders
 * screen cannot carry a Reviews row even as dead metadata: `TAB_META` is keyed
 * by this type, and re-adding a `REVIEW_REQUEST` entry there stops compiling.
 * A runtime-only filter left the row sitting in the source looking live, which
 * is how a second editable copy of one policy gets restored by accident.
 */
export type NavReminderType = Exclude<ReminderType, typeof REVIEW_REQUEST_POLICY_TYPE>;

export const REMINDER_NAV_TYPES: NavReminderType[] = REMINDER_TYPES.filter(
  (type): type is NavReminderType => type !== REVIEW_REQUEST_POLICY_TYPE,
);

export const REMINDER_TYPE_LABELS: Record<ReminderType, string> = {
  JOB_CONFIRMATION: 'Job Confirmation',
  JOB_REMINDER: 'Job Reminder',
  ESTIMATE_FOLLOWUP: 'Estimate Follow-up',
  PROPOSAL_FOLLOWUP: 'Proposal Follow-up',
  UNPAID_INVOICE: 'Unpaid Invoice',
  UNPAID_DEPOSIT: 'Unpaid Deposit',
  REVIEW_REQUEST: 'Review Request',
  PARTS_ORDERED: 'Parts Ordered',
  PARTS_ARRIVED: 'Parts Arrived',
};

export const REMINDER_TYPE_DESCRIPTIONS: Record<ReminderType, string> = {
  JOB_CONFIRMATION: 'Confirms newly scheduled appointments via tap-to-confirm link.',
  JOB_REMINDER:
    'Pre-appointment reminders (T-24h / T-1h) — sent only once the customer has confirmed the visit.',
  ESTIMATE_FOLLOWUP: 'Nudges customers to act on estimates that haven’t been answered.',
  PROPOSAL_FOLLOWUP: 'Follow-ups for Good/Better/Best proposal options.',
  UNPAID_INVOICE: 'Reminders for invoices that are past their due date.',
  UNPAID_DEPOSIT: 'Pre-appointment chases for outstanding job deposits.',
  REVIEW_REQUEST: 'Asks the customer for a rating after job completion.',
  PARTS_ORDERED:
    'Tells the customer their parts are on order, with the expected date. Turn it on in purchasing settings.',
  PARTS_ARRIVED:
    'Tells the customer their parts are in and asks them to book the return visit. Turn it on in purchasing settings.',
};

export type ReminderChannel = 'SMS' | 'EMAIL' | 'VOICE';

export const REMINDER_CHANNELS: ReminderChannel[] = ['SMS', 'EMAIL', 'VOICE'];

export interface CadenceStep {
  offsetMinutes: number;
  channel: ReminderChannel;
  templateKey?: string;
}

export interface QuietHoursConfig {
  startMinute: number;
  endMinute: number;
  tz: string;
  /**
   * Server-managed provenance of `tz` — `seeded` still tracks the company
   * timezone, `explicit` is an operator's own choice. Round-tripped as-is; the
   * backend recomputes it on save, so nothing here should set it. A missing
   * value is legacy/unknown and is preserved as explicit by the backend.
   */
  tzSource?: 'seeded' | 'explicit';
}

export interface TemplateConfig {
  key: string;
  channel: ReminderChannel;
  subject?: string;
  body: string;
}

/** Server response shape for `GET /v2/reminder-policies/:type`. */
export interface ReminderPolicy {
  id: string;
  tenantId: string;
  type: ReminderType;
  enabled: boolean;
  cadence: CadenceStep[];
  maxAttempts: number;
  quietHours: QuietHoursConfig | null;
  aiPersonalization: boolean;
  aiSendTimeOptimization: boolean;
  aiChannelSelection: boolean;
  aiVoiceFallback: boolean;
  templates: TemplateConfig[];
  createdAt?: string;
  updatedAt?: string;
}

/** Body for `PATCH /v2/reminder-policies/:type` — every field is optional. */
export interface UpdateReminderPolicyDto {
  enabled?: boolean;
  cadence?: CadenceStep[];
  maxAttempts?: number;
  quietHours?: QuietHoursConfig | null;
  aiPersonalization?: boolean;
  aiSendTimeOptimization?: boolean;
  aiChannelSelection?: boolean;
  aiVoiceFallback?: boolean;
  templates?: TemplateConfig[];
}
